import { etbToSantim, formatEtb, santimToEtb, type Language } from '@bravo/shared';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePrefsStore } from '../stores/prefs';

const TIME_ZONE = 'Africa/Addis_Ababa';

function dateTimeFormatter(language: Language): Intl.DateTimeFormat {
  const locale = language === 'am' ? 'am-ET' : 'en-GB';
  const options: Intl.DateTimeFormatOptions = {
    timeZone: TIME_ZONE,
    dateStyle: 'medium',
    timeStyle: 'short',
  };
  try {
    return new Intl.DateTimeFormat(locale, options);
  } catch {
    return new Intl.DateTimeFormat('en-GB', options);
  }
}

export function useFormat() {
  const language = usePrefsStore((s) => s.language);
  const { t } = useTranslation();
  return useMemo(() => {
    const dateTime = dateTimeFormatter(language);
    return {
      language,
      etb: (santim: number) => formatEtb(santim, { language }),
      etbBare: (santim: number) => santimToEtb(santim),
      when: (iso?: string) => (iso ? dateTime.format(new Date(iso)) : '—'),
      text: (value: { en: string; am: string } | undefined) =>
        value ? value[language] || value.en || value.am : '',
      status: (value: string) => t(`statuses.${value}`, { defaultValue: value }),
      method: (value: string) => t(`methods.${value}`, { defaultValue: value }),
      category: (value: string) => t(`categories.${value}`, { defaultValue: value }),
      role: (value: string) => t(`roles.${value}`, { defaultValue: value }),
    };
  }, [language, t]);
}

/** Parse an ETB field. Empty is null. Invalid input throws the shared money error. */
export function parseEtbField(input: string): number | null {
  const value = input.trim();
  if (!value) return null;
  return etbToSantim(value);
}
